// 3rd party
import * as Cookies from "es-cookie";
import axios from "axios";
import * as axiosTypes from "axios";
import { Either } from "tsmonad";
import JsonousDecoder, * as jsonous from "jsonous";
import * as resulty from "resulty";

// Helpers
import * as decoding from "./decoding";

// Assets
import defaultAvatar from "@src/persistence/assets/default-user-profile.jpg";

// Domain
import * as domain from "@src/domain";

export { loadUser, logout, loadConfig, loadProductInfo, loadQuantities };

interface ProductInfoResponse {
	version: string;
}

function loadProductInfo(config: domain.Config): Promise<domain.ProductInfo> {
	return axios
		.get(config.infoUrl, getRequestConfig(config))
		.then((productInfoResponse: axiosTypes.AxiosResponse) => {
			const decoder = jsonous
				.field("version", nonEmptyStringDecoder)
				.andThen((version: string) => jsonous.succeed({ version }));
			return decoding.applyJsonousDecoder(
				decoder,
				{
					failure: (error: string): string =>
						`Cannot decode product info. ${error}`,
					success: (result: ProductInfoResponse): ProductInfoResponse => result,
				},
				productInfoResponse.data
			);
		})
		.then(
			(
				decoded: Either<string, ProductInfoResponse>
			): Promise<ProductInfoResponse> =>
				decoded.caseOf({
					left: (error: string): Promise<ProductInfoResponse> =>
						Promise.reject(error),
					right: (
						productInfo: ProductInfoResponse
					): Promise<ProductInfoResponse> => Promise.resolve(productInfo),
				})
		);
}

function loadUser(config: domain.Config): Promise<domain.User> {
	return getSessionInfo(config).then((sessionInfo) => {
		return {
			username: sessionInfo.username,
			avatar: defaultAvatar,
		};
	});
}

function loadQuantities(
	config: domain.Config
): Promise<domain.ProductQuantities> {
	return getModelQuantity(config).then((models: ModelQuantityResponse) => {
		return getDeviceQuantity(config, models.items).then(
			(deviceQuantity: number) => {
				return {
					modelQuantity: models.total,
					deviceQuantity: deviceQuantity,
				};
			}
		);
	});
}

const nonEmptyStringDecoder: JsonousDecoder<string> = jsonous.string.andThen(
	(value: string): JsonousDecoder<string> => {
		if (value.length > 0) {
			return jsonous.succeed(value);
		} else {
			const errorMsg = `Cannot be an empty string`;
			return jsonous.fail(errorMsg);
		}
	}
);
interface SessionInfoResponse {
	username: string;
}

function getSessionInfo(config: domain.Config): Promise<SessionInfoResponse> {
	return axios
		.get(config.sessionInfoUrl, getRequestConfig(config))
		.then((sessionInfoResponse: axiosTypes.AxiosResponse) => {
			const decoder = jsonous
				.field("username", nonEmptyStringDecoder)
				.andThen((username: string) => jsonous.succeed({ username }));
			return decoding.applyJsonousDecoder(
				decoder,
				{
					failure: (error: string): string =>
						`Cannot decode session info. ${error}`,
					success: (result: SessionInfoResponse): SessionInfoResponse => result,
				},
				sessionInfoResponse.data
			);
		})
		.then(
			(
				decoded: Either<string, SessionInfoResponse>
			): Promise<SessionInfoResponse> =>
				decoded.caseOf({
					left: (error: string): Promise<SessionInfoResponse> =>
						Promise.reject(error),
					right: (
						sessionInfo: SessionInfoResponse
					): Promise<SessionInfoResponse> => Promise.resolve(sessionInfo),
				})
		);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unknownDecoder = new JsonousDecoder((value: any) =>
	resulty.ok(value as unknown)
);

const modelDecoder: JsonousDecoder<Model> = jsonous
	.field("name", nonEmptyStringDecoder)
	.andThen((name: string) => jsonous.succeed<Model>({ name }));

interface Model {
	name: string;
}

interface ModelQuantityResponse {
	items: Model[];
	total: number;
}

function getModelQuantity(
	config: domain.Config
): Promise<ModelQuantityResponse> {
	return axios
		.get(config.schemasUrl, getRequestConfig(config))
		.then((response: axiosTypes.AxiosResponse) => {
			const decoder = jsonous
				.field("_items", jsonous.array(modelDecoder))
				.andThen((models: Model[]) =>
					jsonous
						.field("_total", jsonous.number)
						.andThen((total: number) =>
							jsonous.succeed({ items: models, total })
						)
				);
			return decoding.applyJsonousDecoder(
				decoder,
				{
					failure: (error: string): string => `Cannot decode schemas. ${error}`,
					success: (result: ModelQuantityResponse): ModelQuantityResponse =>
						result,
				},
				response.data
			);
		})
		.then(
			(
				decoded: Either<string, ModelQuantityResponse>
			): Promise<ModelQuantityResponse> =>
				decoded.caseOf({
					left: (error: string): Promise<ModelQuantityResponse> =>
						Promise.reject(error),
					right: (
						schemas: ModelQuantityResponse
					): Promise<ModelQuantityResponse> => Promise.resolve(schemas),
				})
		);
}

function getDeviceQuantity(
	config: domain.Config,
	models: Model[]
): Promise<number> {
	const modelResponses: Promise<axiosTypes.AxiosResponse>[] = models.map(
		(model: Model): Promise<axiosTypes.AxiosResponse> =>
			axios.head(`${config.assetsUrl}/${model.name}`, getRequestConfig(config))
	);

	return Promise.all(modelResponses).then(
		(responses: axiosTypes.AxiosResponse[]) =>
			responses.reduce(
				(totalSoFar: number, response: axiosTypes.AxiosResponse): number => {
					const headers = extractResponseHeaders(response);
					const eitherTotal = decodeTotalCount(headers);
					return eitherTotal.caseOf({
						left: (error: string): number => {
							console.log(
								`Error while counting the number of assets: ${error}`
							);
							return totalSoFar;
						},
						right: (total: number): number => totalSoFar + total,
					});
				},
				0
			)
	);
}

function extractResponseHeaders(response: axiosTypes.AxiosResponse): unknown {
	return response.headers;
}

function decodeTotalCount(headers: unknown): Either<string, number> {
	const decoder: JsonousDecoder<number> = jsonous
		.field("x-total-count", jsonous.string)
		.andThen((xTotalCount) =>
			isNaN(parseInt(xTotalCount))
				? jsonous.fail(`The value "${xTotalCount}" can't be parsed as a number`)
				: jsonous.succeed(parseInt(xTotalCount))
		);
	return decoding.applyJsonousDecoder(
		decoder,
		{
			failure: (error: string): string =>
				`Cannot decode response header X-Total-Count: ${error}`,
			success: (totalCount: number): number => totalCount,
		},
		headers
	);
}

function logout(config: domain.Config): Promise<axiosTypes.AxiosResponse> {
	return axios.get(config.logoutUrl, getRequestConfig(config));
}

interface HttpRequestConfig {
	headers: {
		Accept: "application/json";
		"Cache-Control": "no-cache";
		"Access-Token": string | undefined;
	};
}

function getRequestConfig(config: domain.Config): HttpRequestConfig {
	return {
		headers: {
			Accept: "application/json",
			"Cache-Control": "no-cache",
			"Access-Token": Cookies.get(config.sessionCookieName),
		},
	};
}

const moduleDecoder: JsonousDecoder<domain.Module> = jsonous
	.succeed<{ type: "module" }>({ type: "module" })
	.assign("name", jsonous.field("name", nonEmptyStringDecoder))
	.assign("route", jsonous.field("route", nonEmptyStringDecoder))
	.assign("url", jsonous.field("url", nonEmptyStringDecoder))
	.assign("origin", jsonous.field("origin", nonEmptyStringDecoder));

const groupDecoder: JsonousDecoder<domain.Group> = jsonous
	.succeed<{ type: "group" }>({ type: "group" })
	.assign("name", jsonous.field("name", nonEmptyStringDecoder))
	.assign("modules", jsonous.field("modules", jsonous.array(moduleDecoder)));

function decodeMenu(menu: unknown[]): domain.Menu {
	return menu.reduce((soFar: domain.Menu, current): domain.Menu => {
		return decoding
			.applyJsonousDecoder(
				moduleDecoder,
				{
					failure: (error: string): string =>
						`Could not decode module: ${error}`,
					success: (decodedResult: domain.Module): domain.Module =>
						decodedResult,
				},
				current
			)
			.caseOf({
				left: () => {
					return decoding
						.applyJsonousDecoder(
							groupDecoder,
							{
								failure: (error: string): string =>
									`Could not decode group: ${error}`,
								success: (decodedResult: domain.Group): domain.Group =>
									decodedResult,
							},
							current
						)
						.caseOf({
							left: () => soFar,
							right: (group) => [...soFar, group],
						});
				},
				right: (module) => [...soFar, module],
			});
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	}, []);
}

function decodeConfig(config: unknown): Either<string, domain.Config> {
	const decoder: JsonousDecoder<domain.Config> = jsonous
		.field("loginUrl", nonEmptyStringDecoder)
		.andThen((loginUrl: string) =>
			jsonous
				.field("logoutUrl", nonEmptyStringDecoder)
				.andThen((logoutUrl: string) =>
					jsonous
						.field("sessionInfoUrl", nonEmptyStringDecoder)
						.andThen((sessionInfoUrl: string) =>
							jsonous
								.field("sessionCookieName", nonEmptyStringDecoder)
								.andThen((sessionCookieName: string) =>
									jsonous
										.field("schemasUrl", nonEmptyStringDecoder)
										.andThen((schemasUrl: string) =>
											jsonous
												.field("assetsUrl", nonEmptyStringDecoder)
												.andThen((assetsUrl: string) =>
													jsonous
														.field("infoUrl", nonEmptyStringDecoder)
														.andThen((infoUrl: string) =>
															jsonous
																.field("pollingInterval", jsonous.number)
																.andThen((pollingInterval: number) =>
																	jsonous
																		.field(
																			"userManagementUrl",
																			nonEmptyStringDecoder
																		)
																		.andThen((userManagementUrl: string) =>
																			jsonous
																				.field(
																					"menu",
																					jsonous.array(unknownDecoder)
																				)
																				.andThen((menu: unknown[]) =>
																					jsonous.succeed({
																						loginUrl,
																						logoutUrl,
																						sessionInfoUrl,
																						sessionCookieName,
																						schemasUrl,
																						assetsUrl,
																						infoUrl,
																						pollingInterval,
																						userManagementUrl,
																						menu: decodeMenu(menu),
																					})
																				)
																		)
																)
														)
												)
										)
								)
						)
				)
		);
	return decoding.applyJsonousDecoder(
		decoder,
		{
			failure: (error: string): string =>
				`Could not decode configuration: ${error}`,
			success: (decodedResult: domain.Config): domain.Config => decodedResult,
		},
		config
	);
}

function loadConfig(): Promise<domain.Config> {
	return axios
		.get("/config.json", { headers: { "Cache-Control": "no-cache" } })
		.then((data: axiosTypes.AxiosResponse<Record<string, unknown>>) => {
			return data.data;
		})
		.then(decodeConfig)
		.then(
			(result: Either<string, domain.Config>): Promise<domain.Config> =>
				result.caseOf({
					left: (error: string): Promise<domain.Config> =>
						Promise.reject(error),
					right: (config: domain.Config): Promise<domain.Config> =>
						Promise.resolve(config),
				})
		);
}
